import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription,
  AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Users, Search, Shield, Scissors, Handshake, UserCircle, UserPlus, Archive, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import AddUserDialog from './AddUserDialog';

const ROLE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Clients', value: 'client' },
  { label: 'Stylists', value: 'stylist' },
  { label: 'Partners', value: 'partner' },
  { label: 'Admins', value: 'admin' },
];

function roleBadge(role) {
  switch (role) {
    case 'admin':
      return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
    case 'stylist':
      return <Badge className="bg-primary/20 text-primary border-primary/30"><Scissors className="h-3 w-3 mr-1" />Stylist</Badge>;
    case 'partner':
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><Handshake className="h-3 w-3 mr-1" />Partner</Badge>;
    default:
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><UserCircle className="h-3 w-3 mr-1" />Client</Badge>;
  }
}

// Shared body for every admin people-management page (Clients / Stylists / Partners /
// Admins / all Users). Behaviour is identical everywhere — only the config props differ.
export default function AdminUserManagement({
  title,
  subtitle,
  Icon = Users,
  role = '',
  emptyText = 'No users found',
  allowRoleFilter = false,
}) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const effectiveRole = allowRoleFilter ? roleFilter : role;

  const fetchUsers = useCallback(async () => {
    try {
      const params = {};
      if (effectiveRole) params.role = effectiveRole;
      if (search) params.search = search;
      const res = await api.get('/admin/users', { params });
      setUsers(res.data);
    } catch (error) {
      toast.error(`Failed to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [effectiveRole, search, title]);

  // Refetch on role/filter change; search is applied on submit (matches prior behaviour).
  useEffect(() => { fetchUsers(); }, [effectiveRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => { e.preventDefault(); setLoading(true); fetchUsers(); };

  const archiveUser = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User archived');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not archive user');
    } finally {
      setBusyId(null);
    }
  };

  const restoreUser = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/users/${id}/reactivate`);
      toast.success('User restored');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore user');
    } finally {
      setBusyId(null);
    }
  };

  const counts = {
    all: users.length,
    client: users.filter((u) => u.role === 'client').length,
    stylist: users.filter((u) => u.role === 'stylist').length,
    partner: users.filter((u) => u.role === 'partner').length,
    admin: users.filter((u) => u.role === 'admin').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-cormorant text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent flex items-center gap-3">
            <Icon className="h-8 w-8 text-primary" />
            {title}
          </h1>
          <p className="text-muted-foreground mt-1">{subtitle}</p>
        </div>
        <Button className="btn-primary shrink-0" onClick={() => setAddOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </div>

      {allowRoleFilter ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setRoleFilter(f.value); setLoading(true); }}
              className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                roleFilter === f.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border/40 bg-card/30 text-muted-foreground hover:border-primary/30'
              }`}
            >
              {f.label}
              <span className="ml-2 text-xs opacity-70">({f.value === '' ? counts.all : counts[f.value] || 0})</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="p-3 rounded-lg border border-border/40 bg-card/30 text-sm font-medium text-muted-foreground inline-block">
          Total {title}: <span className="text-primary">{users.length}</span>
        </div>
      )}

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>
        <Button type="submit" className="btn-primary">Search</Button>
      </form>

      <Card className="border-border/40">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Name</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Email</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Phone</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Joined</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      <Icon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>{emptyText}</p>
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const isSelf = currentUser?.user_id === u.user_id;
                    const inactive = u.is_active === false;
                    return (
                      <tr
                        key={u.user_id}
                        className={`border-b border-border/20 hover:bg-card/50 transition-colors ${inactive ? 'opacity-50' : ''}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                              {(u.first_name || u.name || u.email || '?')[0].toUpperCase()}
                            </div>
                            <span className="font-medium">
                              {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.name || 'N/A'}
                            </span>
                            {inactive && (
                              <Badge className="bg-muted text-muted-foreground border-border">Inactive</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{u.email}</td>
                        <td className="p-4">{roleBadge(u.role)}</td>
                        <td className="p-4 text-sm text-muted-foreground">{u.phone || '—'}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-4 text-right">
                          {isSelf ? (
                            <span className="text-xs text-muted-foreground">You</span>
                          ) : inactive ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === u.user_id}
                              onClick={() => restoreUser(u.user_id)}
                              className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            >
                              <RotateCcw className="h-4 w-4 mr-1" /> Restore
                            </Button>
                          ) : (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={busyId === u.user_id}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Archive className="h-4 w-4 mr-1" /> Archive
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Archive this user?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {(u.name || u.email)} will be deactivated and hidden from active lists.
                                    Their records are kept and you can restore them at any time.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => archiveUser(u.user_id)}>Archive</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AddUserDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultRole={allowRoleFilter ? 'client' : (role || 'client')}
        onCreated={fetchUsers}
      />
    </div>
  );
}
